/**
 * Page /mes-demandes-urbanisme — espace administré (accès citoyen).
 * Dépôt d'une demande (CU/DP/PC/PA) + pièces, suivi de SES dossiers avec frise
 * des statuts, annulation, et consultation des documents d'urbanisme (PLU…).
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { AppHeader } from '@/components/AppHeader';
import { Card, Button, Badge, Alert, SectionEyebrow, Field, Input } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { UrbaDemande, UrbaType, UrbaStatut, UrbaDocument } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { AlertCircle, FileText, Download, Plus, X, CheckCircle2, ExternalLink, Clock } from 'lucide-react';

const TYPES: { value: UrbaType; code: string; label: string }[] = [
  { value: 'CU', code: 'CU', label: 'Certificat d\'urbanisme' },
  { value: 'DP', code: 'DP', label: 'Déclaration préalable' },
  { value: 'PC', code: 'PC', label: 'Permis de construire' },
  { value: 'PA', code: 'PA', label: 'Permis d\'aménager' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));
const DOC_TYPE_LABEL: Record<string, string> = {
  plu: 'PLU / PLUi', carte_communale: 'Carte communale', zonage: 'Zonage',
  reglement: 'Règlement', cerfa: 'CERFA', notice: 'Notice', autre: 'Document',
};

const STATUT_ORDER: UrbaStatut[] = ['deposee', 'complete', 'instruction', 'accordee'];
function statutBadge(s: UrbaStatut): { label: string; variant: 'success' | 'warning' | 'neutral' | 'urba' | 'urgent' } {
  switch (s) {
    case 'deposee': return { label: 'Déposée', variant: 'neutral' };
    case 'complete': return { label: 'Complète', variant: 'urba' };
    case 'incomplete': return { label: 'Incomplète', variant: 'warning' };
    case 'instruction': return { label: 'En instruction', variant: 'warning' };
    case 'accordee': return { label: 'Accordée', variant: 'success' };
    case 'refusee': return { label: 'Refusée', variant: 'urgent' };
    case 'annulee': return { label: 'Annulée', variant: 'neutral' };
  }
}
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris' });
}

export function MesDemandesUrbanismePage() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [demandes, setDemandes] = useState<UrbaDemande[]>([]);
  const [documents, setDocuments] = useState<UrbaDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState<UrbaDemande | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [d, docs] = await Promise.all([api.mesDemandesUrbanisme.list(), api.mesDemandesUrbanisme.listDocuments()]);
      setDemandes(d.demandes); setDocuments(docs.documents);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function openDetail(d: UrbaDemande) {
    try { setSelected(await api.mesDemandesUrbanisme.get(d.id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function annuler(d: UrbaDemande) {
    const ok = await confirm({ title: 'Annuler cette demande ?', description: 'Vous ne pourrez plus la modifier. Action définitive.', danger: true, confirmLabel: 'Annuler la demande' });
    if (!ok) return;
    try { await api.mesDemandesUrbanisme.annuler(d.id); setSelected(null); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function openPiece(id: string) {
    try { const { url } = await api.mesDemandesUrbanisme.pieceUrl(id); window.open(url, '_blank', 'noopener'); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function openArrete(id: string) {
    try { const { url } = await api.mesDemandesUrbanisme.arreteUrl(id); window.open(url, '_blank', 'noopener'); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function openDocument(d: UrbaDocument) {
    if (!d.aFichier) {
      if (d.urlExterne) { window.open(d.urlExterne, '_blank', 'noopener'); return; }
      if (d.lienGpu) window.open(d.lienGpu, '_blank', 'noopener');
      return;
    }
    try { const { url } = await api.mesDemandesUrbanisme.documentUrl(d.id); window.open(url, '_blank', 'noopener'); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Urbanisme" accent="urba" />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        <SectionEyebrow as="div">Saisine par voie électronique</SectionEyebrow>
        <h1 className="mt-1.5 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">Mes demandes d'urbanisme</h1>
        <p className="mt-2 text-sm text-ink-muted">Déposez vos demandes (permis, déclarations, certificats) et suivez leur instruction.</p>

        {error && <Alert variant="error" icon={AlertCircle} className="mt-4">{error}</Alert>}

        <div className="mt-5 flex justify-end">
          <Button variant="primary" onClick={() => setShowForm((s) => !s)}>
            {showForm ? <><X className="h-4 w-4" /> Fermer</> : <><Plus className="h-4 w-4" /> Nouvelle demande</>}
          </Button>
        </div>

        {showForm && <DepotForm onDone={() => { setShowForm(false); void reload(); }} onError={setError} />}

        <h2 className="mt-8 font-display text-base font-semibold text-ink mb-3">Mes dossiers</h2>
        {loading ? (
          <Card variant="glass" padded><p className="text-sm text-ink-muted">Chargement…</p></Card>
        ) : demandes.length === 0 ? (
          <Card variant="glass" padded><p className="text-sm text-ink-muted">Vous n'avez pas encore déposé de demande.</p></Card>
        ) : (
          <div className="space-y-3">
            {demandes.map((d) => {
              const sb = statutBadge(d.statut); const isSel = selected?.id === d.id;
              return (
                <Card key={d.id} variant="glass" padded>
                  <button type="button" onClick={() => (isSel ? setSelected(null) : openDetail(d))} className="flex w-full items-center gap-3 text-left">
                    <Badge variant="urba" size="sm">{d.typeDemande}</Badge>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-ink">{d.description?.slice(0, 60) || TYPE_LABEL[d.typeDemande]}</div>
                      <div className="font-mono text-xs text-ink-dim">{d.numero} · déposée le {fmtDate(d.createdAt)}</div>
                    </div>
                    <Badge variant={sb.variant} size="sm">{sb.label}</Badge>
                  </button>

                  {isSel && selected && (
                    <div className="mt-4 border-t border-border pt-4">
                      {selected.statut !== 'refusee' && selected.statut !== 'annulee' && (<Frise statut={selected.statut} />)}
                      {selected.statut === 'incomplete' && (
                        <div className="rounded-r-md border border-warning/30 bg-warning-tint/40 p-3 text-sm">
                          <div className="font-medium text-warning">📩 La mairie attend des pièces complémentaires</div>
                          {selected.piecesDemandees && <div className="mt-1.5 whitespace-pre-line text-ink-muted">{selected.piecesDemandees}</div>}
                          {selected.piecesMessage && <div className="mt-1.5 text-ink-muted">« {selected.piecesMessage} »</div>}
                          <div className="mt-1.5 text-2xs text-ink-dim">Le délai d'instruction reprendra à la réception de vos pièces.</div>
                          <AjoutPieces demandeId={selected.id} onError={setError} onDone={() => void reload()} />
                        </div>
                      )}
                      {selected.statut === 'refusee' && (
                        <div className="rounded-r-md border border-urgent/30 bg-urgent-tint/40 p-3 text-sm text-urgent">
                          Demande refusée{selected.motifDecision ? ` — ${selected.motifDecision}` : ''}.
                        </div>
                      )}
                      {selected.statut === 'annulee' && <div className="text-sm text-ink-muted">Demande annulée.</div>}

                      {(selected.statut === 'accordee' || selected.statut === 'refusee') && selected.arreteGenereAt && (
                        <div className="mt-3 flex items-center gap-3 rounded-r-md border border-border p-3">
                          <div className="flex h-9 w-9 flex-none items-center justify-center rounded-r-lg bg-tech-tint text-tech-fg"><FileText className="h-5 w-5" /></div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-ink">Arrêté — {selected.numero}</div>
                            <div className="text-xs text-ink-muted">décision du {selected.decisionAt ? fmtDate(selected.decisionAt) : '—'}</div>
                          </div>
                          <Button variant="primary" size="sm" onClick={() => openArrete(selected.id)}><Download className="h-4 w-4" /> Télécharger</Button>
                        </div>
                      )}

                      {selected.pieces && selected.pieces.length > 0 && (
                        <div className="mt-3">
                          <div className="text-2xs uppercase tracking-r-eyebrow text-ink-dim mb-1">Mes pièces</div>
                          <div className="space-y-1.5">
                            {selected.pieces.map((p) => (
                              <button key={p.id} type="button" onClick={() => openPiece(p.id)} className="flex w-full items-center gap-2 rounded-r-md border border-border bg-surface/50 px-3 py-2 text-left text-sm hover:border-border-strong">
                                <FileText className="h-4 w-4 flex-none text-urba-fg" />
                                <span className="min-w-0 flex-1 truncate text-ink">{p.nomFichier ?? 'pièce'}</span>
                                <Download className="h-4 w-4 flex-none text-ink-dim" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {['deposee', 'complete', 'incomplete', 'instruction'].includes(selected.statut) && (
                        <div className="mt-3 flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => annuler(selected)} className="text-ink-muted hover:text-urgent"><X className="h-4 w-4" /> Annuler ma demande</Button>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {documents.length > 0 && (
          <>
            <h2 className="mt-8 font-display text-base font-semibold text-ink mb-1">Documents de la commune</h2>
            <p className="text-sm text-ink-muted mb-3">Consultez le PLU, les zonages et les formulaires avant de déposer.</p>
            <Card variant="glass" padded>
              <div className="divide-y divide-border">
                {documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 py-3">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-r-lg bg-urba-tint text-urba-fg"><FileText className="h-5 w-5" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium text-ink">{d.titre}</span>
                        <Badge variant="urba" size="sm">{d.typeDoc === 'autre' && d.typeLibre ? d.typeLibre : DOC_TYPE_LABEL[d.typeDoc] ?? d.typeDoc}</Badge>
                        {d.cerfaNumero && <Badge variant="tech" size="sm">CERFA {d.cerfaNumero}</Badge>}
                      </div>
                    </div>
                    {d.lienGpu && <a href={d.lienGpu} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-tech-fg"><ExternalLink className="h-3.5 w-3.5" /> Géoportail</a>}
                    {d.urlExterne && <Button variant="ghost" size="sm" onClick={() => openDocument(d)}><ExternalLink className="h-4 w-4" /> Ouvrir</Button>}
                    {d.aFichier && <Button variant="ghost" size="sm" onClick={() => openDocument(d)}><Download className="h-4 w-4" /> Télécharger</Button>}
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </main>
      {dialogElement}
    </div>
  );
}

function Frise({ statut }: { statut: UrbaStatut }) {
  const steps: { key: UrbaStatut; label: string }[] = [
    { key: 'deposee', label: 'Déposée' },
    { key: 'complete', label: 'Complète' },
    { key: 'instruction', label: 'En instruction' },
    { key: 'accordee', label: 'Décision' },
  ];
  const currentIdx = statut === 'incomplete' ? 0 : Math.max(0, STATUT_ORDER.indexOf(statut));
  return (
    <div className="border-l-2 border-border pl-4 ml-1.5">
      {steps.map((s, i) => {
        const done = i <= currentIdx;
        return (
          <div key={s.key} className="flex items-start gap-2.5 py-1.5">
            <span className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full text-2xs text-white ${done ? 'bg-success' : 'bg-border-strong'}`}>
              {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3 w-3" />}
            </span>
            <div>
              <div className={`text-sm ${done ? 'text-ink' : 'text-ink-dim'}`}>{s.label}</div>
              {s.key === statut && statut === 'instruction' && <div className="text-2xs text-ink-dim">en cours d'instruction</div>}
            </div>
          </div>
        );
      })}
      {statut === 'incomplete' && <div className="mt-1 text-xs text-warning">Dossier incomplet — des pièces sont attendues.</div>}
    </div>
  );
}

function DepotForm({ onDone, onError }: { onDone: () => void; onError: (m: string) => void }) {
  const [type, setType] = useState<UrbaType>('CU');
  const [cuNature, setCuNature] = useState<'informatif' | 'operationnel'>('informatif');
  const [pcmi, setPcmi] = useState(false);
  const [parcelle, setParcelle] = useState('');
  const [surface, setSurface] = useState('');
  const [adresse, setAdresse] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function submit() {
    if (description.trim().length < 1 && !parcelle.trim()) { onError('Décrivez votre projet ou indiquez la parcelle'); return; }
    setBusy(true); onError('');
    try {
      const dem = await api.mesDemandesUrbanisme.create({
        typeDemande: type,
        cuNature: type === 'CU' ? cuNature : null,
        pcmi: type === 'PC' ? pcmi : false,
        parcelle: parcelle.trim() || null,
        surfaceM2: surface.trim() ? Number(surface) : null,
        adresseTerrain: adresse.trim() || null,
        description: description.trim() || null,
      });
      for (const f of files) {
        const c = await compressImageIfNeeded(f);
        await api.mesDemandesUrbanisme.addPiece(dem.id, c);
      }
      onDone();
    } catch (e) { onError(e instanceof ApiException ? e.message : 'Dépôt impossible'); }
    finally { setBusy(false); }
  }

  return (
    <Card variant="glass" padded className="mt-4">
      <h2 className="font-display text-base font-semibold text-ink mb-3">Déposer une nouvelle demande</h2>
      <div className="mb-3">
        <label className="block text-sm font-medium text-ink mb-1.5">Type de demande</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {TYPES.map((t) => (
            <button key={t.value} type="button" onClick={() => setType(t.value)}
              className={`rounded-r-lg border px-2 py-2.5 text-center transition-all ${type === t.value ? 'border-urba bg-urba-tint' : 'border-border bg-surface/50 hover:border-border-strong'}`}>
              <div className="font-display text-sm font-bold text-urba-fg">{t.code}</div>
              <div className="text-2xs text-ink-muted">{t.label}</div>
            </button>
          ))}
        </div>
      </div>

      {type === 'CU' && (
        <div className="mb-3">
          <label className="block text-sm font-medium text-ink mb-1.5">Nature du certificat d'urbanisme</label>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setCuNature('informatif')}
              className={`rounded-r-pill border px-3 py-1.5 text-xs ${cuNature === 'informatif' ? 'border-urba bg-urba-tint text-urba-fg' : 'border-border-strong text-ink-muted'}`}>Informatif (CUa) — délai 1 mois</button>
            <button type="button" onClick={() => setCuNature('operationnel')}
              className={`rounded-r-pill border px-3 py-1.5 text-xs ${cuNature === 'operationnel' ? 'border-urba bg-urba-tint text-urba-fg' : 'border-border-strong text-ink-muted'}`}>Opérationnel (CUb) — délai 2 mois</button>
          </div>
          <p className="mt-1 text-2xs text-ink-dim">Le CU informatif renseigne les règles applicables ; l'opérationnel précise si un projet précis est réalisable.</p>
        </div>
      )}

      {type === 'PC' && (
        <label className="mb-3 flex items-center gap-2 text-sm text-ink cursor-pointer">
          <input type="checkbox" checked={pcmi} onChange={(e) => setPcmi(e.target.checked)} className="h-4 w-4 rounded border-border-strong text-urba" />
          Permis pour une maison individuelle (délai 2 mois au lieu de 3)
        </label>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Référence cadastrale (parcelle)" htmlFor="u-parc"><Input id="u-parc" value={parcelle} onChange={(e) => setParcelle(e.target.value)} placeholder="AB 0142" /></Field>
        <Field label="Surface concernée (m²)" htmlFor="u-surf"><Input id="u-surf" type="number" inputMode="numeric" value={surface} onChange={(e) => setSurface(e.target.value)} placeholder="620" /></Field>
      </div>
      <Field label="Adresse du terrain" htmlFor="u-adr"><Input id="u-adr" value={adresse} onChange={(e) => setAdresse(e.target.value)} placeholder="12 rue des Charentes" /></Field>
      <Field label="Description du projet" htmlFor="u-desc">
        <textarea id="u-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
          className="w-full rounded-r-lg border border-border bg-surface/60 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-urba" placeholder="Construction d'un abri de jardin de 18 m²…" />
      </Field>
      <Field label="Pièces jointes (PDF ou images)" htmlFor="u-files">
        <input ref={fileRef} id="u-files" type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          className="block w-full text-sm text-ink-muted file:mr-3 file:rounded-r-md file:border-0 file:bg-urba-tint file:px-3 file:py-2 file:text-sm file:text-urba-fg" />
        {files.length > 0 && <p className="mt-1 text-2xs text-ink-dim">{files.length} fichier(s) — images compressées avant l'envoi.</p>}
      </Field>
      <div className="flex justify-end">
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? 'Dépôt…' : 'Déposer ma demande'}</Button>
      </div>
    </Card>
  );
}

/**
 * AjoutPieces — permet à l'administré de téléverser des pièces complémentaires
 * quand son dossier est « incomplet ». Réutilise l'endpoint existant
 * (POST /mes-demandes-urbanisme/:id/pieces) + la compression d'images.
 * À la réception, le backend recalcule complet_at (reprise du délai).
 */
function AjoutPieces({ demandeId, onError, onDone }: { demandeId: string; onError: (m: string) => void; onDone: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function envoyer() {
    if (files.length === 0) return;
    setBusy(true); onError(''); setOk(false);
    try {
      for (const f of files) {
        const c = await compressImageIfNeeded(f);
        await api.mesDemandesUrbanisme.addPiece(demandeId, c, f.name);
      }
      setFiles([]); if (fileRef.current) fileRef.current.value = '';
      setOk(true);
      onDone();
    } catch (e) {
      onError(e instanceof ApiException ? e.message : 'Envoi des pièces impossible');
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-3 border-t border-warning/20 pt-3">
      <div className="mb-1.5 text-2xs font-medium uppercase tracking-r-eyebrow text-ink-dim">Ajouter mes pièces</div>
      <input
        ref={fileRef} type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp"
        onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
        className="block w-full text-sm text-ink file:mr-3 file:rounded-r-md file:border-0 file:bg-urba file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:bg-urba-dark"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-2xs text-ink-dim">
          {files.length > 0 ? `${files.length} fichier(s) prêt(s) — images compressées avant l'envoi.` : 'PDF ou images (JPEG, PNG, WebP).'}
          {ok && <span className="ml-2 text-success">Pièces envoyées ✓</span>}
        </span>
        <Button variant="primary" size="sm" onClick={envoyer} disabled={busy || files.length === 0}>
          {busy ? 'Envoi…' : 'Envoyer mes pièces'}
        </Button>
      </div>
    </div>
  );
}
